<template>
  <div class="w-full p-4">
    <!-- The band stays on screen, as on My Expenses. -mt-4 cancels the
         page's own p-4: without it the band rests 16px below where it pins
         and the whole page slides that far before it catches. -->
    <div ref="topStickyRef" class="sticky top-14 z-30 bg-gray-200 -mx-4 -mt-4 px-4 pt-3 pb-4">
      <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-2">
        <h2 class="text-lg font-semibold">Roadmap</h2>
      </div>

      <p class="text-sm text-gray-500">
        Epics and milestones by target quarter — the themes the team has
        agreed to work on, and the dates they are working toward.
      </p>

      <!-- Labels above each control, on the backlog board's metrics: h-9,
           border-gray-400, rounded-md. items-end keeps the row aligned on
           the controls, since the labels add height above. -->
      <div class="flex flex-row flex-wrap gap-3 mt-3 items-end text-sm">
        <label class="block">
          <span class="block text-xs text-gray-500 mb-0.5">From</span>
          <select
            v-model="fromQuarter"
            class="h-9 px-2 border border-gray-400 rounded-md"
            @change="reconcile('from')"
          >
            <option value="">Any earlier</option>
            <option v-for="q in quarterOptions" :key="'from-' + q" :value="q">{{ q }}</option>
          </select>
        </label>
        <label class="block">
          <span class="block text-xs text-gray-500 mb-0.5">To</span>
          <select
            v-model="toQuarter"
            class="h-9 px-2 border border-gray-400 rounded-md"
            @change="reconcile('to')"
          >
            <option value="">Any later</option>
            <option v-for="q in quarterOptions" :key="'to-' + q" :value="q">{{ q }}</option>
          </select>
        </label>

        <!-- Unscheduled sits in no quarter, so a date range can neither
             include nor exclude it on its own. Its own switch, on by
             default: hiding it silently would lose the four milestones
             nobody has dated yet. -->
        <label class="flex items-center gap-1.5 h-9 cursor-pointer">
          <input v-model="includeUnscheduled" type="checkbox" class="h-4 w-4" />
          <span>Include unscheduled</span>
        </label>

        <!-- Not filters of their own, two ways of setting the pair above,
             so they read as links rather than controls. Lit when the range
             is exactly what they set, and clicking a lit one is a no-op
             rather than a surprise. -->
        <div class="flex items-center gap-3 h-9">
          <button
            class="text-xs underline"
            :class="isDefaultRange
              ? 'text-dark-primary font-semibold'
              : 'text-gray-500 hover:text-gray-700'"
            :title="`Show ${currentQuarter} and everything after it`"
            @click="showDefaultRange"
          >
            This quarter &amp; forward
          </button>
          <button
            class="text-xs underline"
            :class="isEverything
              ? 'text-dark-primary font-semibold'
              : 'text-gray-500 hover:text-gray-700'"
            title="Show every quarter, past and future"
            @click="showEverything"
          >
            All time
          </button>
        </div>
      </div>

      <!-- Said out loud, because a page that opens on this quarter looks
           identical to a roadmap with no history in it. -->
      <p v-if="hiddenNote" class="text-xs text-gray-500 mt-2">{{ hiddenNote }}</p>
    </div>

    <p v-if="loading" class="mt-6 text-gray-500">Loading…</p>
    <p v-else-if="error" class="mt-6 text-red-600">{{ error }}</p>

    <template v-else>
      <p v-if="!epics.length && !milestones.length" class="mt-6 text-gray-500">
        Nothing on the roadmap yet.
      </p>
      <p v-else-if="!quarters.length" class="mt-6 text-gray-500">
        Nothing on the roadmap in this range —
        <button class="underline" @click="showEverything">show all time</button>.
      </p>

      <div v-for="quarter in quarters" :key="quarter.key" class="mt-6">
        <h3
          class="font-semibold text-dark-primary border-b border-gray-300 pb-1 mb-2
                 flex justify-between bg-gray-200 sticky z-20"
          :style="{ top: (topStickyHeight + NAVBAR_HEIGHT) + 'px' }"
        >
          <span>{{ quarter.label }}</span>
          <span class="text-sm font-normal text-gray-500">{{ quarter.summary }}</span>
        </h3>

        <!-- Milestones first: a quarter's milestones are the dates the
             epics under them are working toward, so they read as the
             header for the quarter rather than a footnote after it. -->
        <div
          v-for="ms in quarter.milestones"
          :key="'milestone-' + ms.id"
          class="border border-amber-300 rounded-md bg-amber-50 px-3 py-2 mb-1.5"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0 flex items-start gap-2">
              <flag-icon class="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <div class="min-w-0">
                <span class="font-medium">{{ ms.title }}</span>
                <p v-if="ms.description" class="text-xs text-gray-600 mt-0.5 whitespace-pre-line">
                  {{ truncate(ms.description) }}
                </p>
              </div>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <span v-if="ms.target_date" class="text-xs text-amber-800">
                {{ formatDate(ms.target_date) }}
              </span>
              <span class="text-xs px-2 py-0.5 rounded" :class="statusChip(ms.status)">
                {{ labelFor(ms.status) }}
              </span>
            </div>
          </div>
        </div>

        <div
          v-for="epic in quarter.rows"
          :key="epic.id"
          class="border border-gray-300 rounded-md bg-white px-3 py-2 mb-1.5 hover:bg-gray-50"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <span class="font-medium">{{ epic.title }}</span>
              <p v-if="epic.description" class="text-xs text-gray-600 mt-0.5 whitespace-pre-line">
                {{ truncate(epic.description) }}
              </p>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <span class="text-xs px-2 py-0.5 rounded" :class="statusChip(epic.status)">
                {{ labelFor(epic.status) }}
              </span>
              <span class="text-xs text-gray-500">
                {{ childCounts[epic.id] || 0 }} item{{ (childCounts[epic.id] || 0) === 1 ? '' : 's' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

import { FlagIcon } from '@heroicons/vue/20/solid'

import { formatDate } from '@/composables/formatDate'
import { useStickyOffset } from '@/composables/useStickyOffset'
import { getWorkItems } from '@/services/BackOffice'

// The app's navbar is fixed at this height; every sticky thing on the
// expenses screens is offset by it, and so is the backlog board.
const NAVBAR_HEIGHT = 56

// Measured rather than assumed: the band wraps differently at narrow widths,
// and a quarter heading offset by a stale number either overlaps it or
// floats below it.
const topStickyRef = ref(null)
const topStickyHeight = useStickyOffset(topStickyRef)

const STATUS_LABELS = {
  backlog: 'Backlog', planned: 'Planned', in_progress: 'In Progress',
  test_review: 'Test / Review', done: 'Done', wont_do: "Won't Do",
}
const STATUS_CHIPS = {
  backlog: 'bg-gray-100 text-gray-700',
  planned: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  test_review: 'bg-purple-100 text-purple-800',
  done: 'bg-green-100 text-green-800',
  wont_do: 'bg-gray-200 text-gray-600',
}

const store = useStore()
const epics = ref([])
const milestones = ref([])
const childCounts = ref({})
const loading = ref(true)
const error = ref('')

// Which quarter we are in, from the local clock rather than from the data:
// the default view has to mean "now" even in a quarter nobody has planned
// anything into yet.
function quarterLabelFor(date) {
  return `${date.getFullYear()} Q${Math.floor(date.getMonth() / 3) + 1}`
}
const currentQuarter = quarterLabelFor(new Date())

// Quarters compare as numbers rather than as strings so that a range can be
// tested with <= : "2026 Q4" and "2027 Q1" happen to sort correctly as text,
// but only because the years are the same length.
function quarterIndex(label) {
  const match = /^(\d{4}) Q([1-4])$/.exec(String(label || ''))
  if (!match) return null
  return (Number(match[1]) * 4) + (Number(match[2]) - 1)
}

// The range, as two bounds. The empty string means unbounded on that side,
// which is what makes the default -- this quarter, nothing capped above it --
// expressible in the same two controls that also look backwards.
const fromQuarter = ref(currentQuarter)
const toQuarter = ref('')
const includeUnscheduled = ref(true)

// Every quarter there is something in, plus the current one even when it is
// empty: a From list that cannot offer today is a list that cannot express
// the default it opens on.
const quarterOptions = computed(() => {
  const labels = new Set([currentQuarter])
  for (const row of [...epics.value, ...milestones.value]) {
    const label = quarterOf(row)
    if (label !== 'Unscheduled') labels.add(label)
  }
  return [...labels].sort((a, b) => quarterIndex(a) - quarterIndex(b))
})

function inRange(label) {
  if (label === 'Unscheduled') return includeUnscheduled.value
  const index = quarterIndex(label)
  if (index === null) return includeUnscheduled.value
  const from = quarterIndex(fromQuarter.value)
  const to = quarterIndex(toQuarter.value)
  if (from !== null && index < from) return false
  if (to !== null && index > to) return false
  return true
}

// An inverted range matches nothing, which reads as a broken page rather
// than as a mistake, so the other end moves out of the way instead.
function reconcile(changed) {
  const from = quarterIndex(fromQuarter.value)
  const to = quarterIndex(toQuarter.value)
  if (from === null || to === null || from <= to) return
  if (changed === 'from') toQuarter.value = fromQuarter.value
  else fromQuarter.value = toQuarter.value
}

const isDefaultRange = computed(
  () => fromQuarter.value === currentQuarter && toQuarter.value === '',
)
const isEverything = computed(
  () => fromQuarter.value === '' && toQuarter.value === '',
)

function showDefaultRange() {
  fromQuarter.value = currentQuarter
  toQuarter.value = ''
  includeUnscheduled.value = true
}

function showEverything() {
  fromQuarter.value = ''
  toQuarter.value = ''
  includeUnscheduled.value = true
}

function labelFor(v) { return STATUS_LABELS[v] || v }
function statusChip(v) { return STATUS_CHIPS[v] || '' }
function truncate(text) {
  return text.length > 220 ? `${text.slice(0, 220)}…` : text
}

// Which quarter a row belongs to. Epics carry target_quarter; milestones
// in practice carry a target_date instead, so the quarter is derived from
// it rather than left blank -- a milestone dated 8 Nov belongs in 2026 Q4
// whether or not anybody typed that in.
//
// The date is split rather than passed to Date(): "2026-10-18" parses as
// UTC midnight, which is the previous evening here, and a milestone dated
// the 1st of a quarter would land in the one before it.
function quarterOf(row) {
  if (row.target_quarter) return row.target_quarter
  if (row.target_date) {
    const [year, month] = String(row.target_date).split('-')
    const q = Math.floor((Number(month) - 1) / 3) + 1
    if (year && q >= 1 && q <= 4) return `${year} Q${q}`
  }
  return 'Unscheduled'
}

const allQuarters = computed(() => {
  const buckets = new Map()
  const bucket = (key) => {
    if (!buckets.has(key)) buckets.set(key, { rows: [], milestones: [] })
    return buckets.get(key)
  }
  for (const epic of epics.value) bucket(quarterOf(epic)).rows.push(epic)
  // Soonest first within the quarter; a milestone with no date trails the
  // dated ones rather than sorting as if it were the earliest.
  const dated = [...milestones.value].sort((a, b) => {
    if (!a.target_date) return 1
    if (!b.target_date) return -1
    return a.target_date.localeCompare(b.target_date)
  })
  for (const ms of dated) bucket(quarterOf(ms)).milestones.push(ms)

  const out = [...buckets.entries()].map(([label, group]) => ({
    key: label,
    label,
    rows: group.rows,
    milestones: group.milestones,
    summary: [
      group.milestones.length
        ? `${group.milestones.length} milestone${group.milestones.length === 1 ? '' : 's'}`
        : '',
      `${group.rows.length} epic${group.rows.length === 1 ? '' : 's'}`,
    ].filter(Boolean).join(' · '),
  }))
  // Quarters sort naturally as "2026 Q3"; Unscheduled belongs last rather
  // than wherever the letter U happens to fall.
  out.sort((a, b) => {
    if (a.label === 'Unscheduled') return 1
    if (b.label === 'Unscheduled') return -1
    return a.label.localeCompare(b.label)
  })
  return out
})

const quarters = computed(() => allQuarters.value.filter(q => inRange(q.label)))

// What the range is keeping off screen, counted in rows rather than in
// quarters: "2 quarters hidden" is true of both one stale epic and half the
// year's work.
const hiddenNote = computed(() => {
  const hidden = allQuarters.value.filter(q => !inRange(q.label))
  if (!hidden.length) return ''
  const items = hidden.reduce((n, q) => n + q.rows.length + q.milestones.length, 0)
  if (!items) return ''
  const quarterWord = hidden.length === 1 ? 'quarter' : 'quarters'
  const itemWord = items === 1 ? 'item' : 'items'
  return `${hidden.length} ${quarterWord} hidden by this range · ${items} ${itemWord}`
})

onMounted(async () => {
  const token = store.getters['auth/showToken']
  try {
    const all = await getWorkItems(token, { get_all: 'true' })
    const rows = Array.isArray(all) ? all : (all.results || [])
    epics.value = rows.filter(r => r.kind === 'epic')
    milestones.value = rows.filter(r => r.kind === 'milestone')

    const counts = {}
    for (const row of rows) {
      if (row.parent) counts[row.parent] = (counts[row.parent] || 0) + 1
    }
    childCounts.value = counts
  } catch (e) {
    error.value = e?.response?.status
      ? `${e.response.status} ${e.response.statusText || ''}`.trim()
      : String(e)
  } finally {
    loading.value = false
  }
})
</script>
